import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Pause, Play, Download, ExternalLink } from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useTenant } from './context';
import { useLogs } from '@/api/logs';
import type { LogLevel } from '@/api/types';
import { cn } from '@/lib/cn';

const LEVEL_TONE: Record<LogLevel, 'neutral' | 'accent' | 'warn' | 'danger'> = {
  info: 'neutral',
  debug: 'neutral',
  warn: 'warn',
  error: 'danger',
};

const ALL_LEVELS: LogLevel[] = ['info', 'warn', 'error', 'debug'];

export function TenantLogs() {
  const { tenant } = useTenant();
  const [q, setQ] = useState('');
  const [levels, setLevels] = useState<Set<LogLevel>>(new Set(ALL_LEVELS));
  const [live, setLive] = useState(true);

  const { data: logs = [], isLoading } = useLogs(
    {
      tenant_id: tenant.id,
      level: ALL_LEVELS.filter(l => levels.has(l)),
      q: q || undefined,
      limit: 500,
    },
    { live },
  );

  function toggleLevel(l: LogLevel) {
    setLevels(prev => {
      const next = new Set(prev);
      if (next.has(l)) next.delete(l);
      else next.add(l);
      return next;
    });
  }

  return (
    <PageBody>
      <Card>
        <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-xs min-w-[200px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Filter…"
              className="w-full h-7 pl-7 pr-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none"
            />
          </div>

          <div className="flex items-center gap-1">
            {(['error', 'warn', 'info', 'debug'] as LogLevel[]).map(l => (
              <button
                key={l}
                onClick={() => toggleLevel(l)}
                className={cn(
                  'h-6 px-2 text-2xs font-semibold uppercase rounded border transition-colors',
                  levels.has(l)
                    ? 'bg-surface-2 border-border-strong text-fg'
                    : 'border-transparent text-fg-subtle hover:bg-surface-2/50',
                )}
              >
                {l}
              </button>
            ))}
          </div>

          <Button
            variant={live ? 'primary' : 'secondary'}
            size="sm"
            iconLeft={live ? <Pause size={12} /> : <Play size={12} />}
            onClick={() => setLive(!live)}
          >
            {live ? 'Live tail · on' : 'Live tail'}
          </Button>

          <Button variant="ghost" size="sm" iconLeft={<Download size={13} />}>Export</Button>

          <Link
            to={`/logs?tenant=${tenant.id}`}
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs text-fg-muted hover:text-fg hover:bg-surface-2 ml-auto"
          >
            Open in full Logs Explorer <ExternalLink size={11} />
          </Link>
        </div>

        <div className="bg-bg-elevated font-mono text-xs leading-relaxed py-2 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="px-4 py-12 text-center text-sm text-fg-muted font-sans">Loading logs…</div>
          ) : logs.length === 0 ? (
            <div className="px-4 py-16 text-center font-sans">
              <div className="text-sm font-semibold text-fg mb-1">No logs to show</div>
              <p className="text-xs text-fg-muted max-w-md mx-auto leading-relaxed">
                Log ingestion isn't wired yet. Once each NousViz workspace exposes{' '}
                <code className="font-mono">/admin/v1/logs</code> (or ships to a central
                collector), they'll appear here.
              </p>
            </div>
          ) : (
            logs.map(line => (
              <div
                key={line.id}
                className="px-4 py-0.5 hover:bg-surface-2/50 flex items-baseline gap-3 group"
              >
                <span className="text-fg-subtle shrink-0">{line.ts.slice(11, 19)}</span>
                <Badge tone={LEVEL_TONE[line.level]} className="shrink-0 uppercase">
                  {line.level}
                </Badge>
                <span className="shrink-0 px-1.5 h-4 rounded text-2xs font-mono bg-surface-2 text-fg-muted">
                  {line.source}
                </span>
                <Link
                  to={`/tenants/${tenant.slug}/workspaces/${line.workspace_id}`}
                  className="shrink-0 px-1.5 h-4 rounded text-2xs font-mono bg-surface-2 text-fg-muted hover:text-accent hover:bg-[color:var(--accent-soft)]"
                >
                  {line.workspace_name.length > 24 ? line.workspace_name.slice(0, 24) + '…' : line.workspace_name}
                </Link>
                <span className="text-fg break-all">{line.msg}</span>
              </div>
            ))
          )}
        </div>
      </Card>

      <p className="mt-3 text-2xs text-fg-subtle">
        Scoped to <strong>{tenant.name}</strong> · {logs.length} log lines · click "Open in full Logs Explorer" for cross-tenant view.
      </p>
    </PageBody>
  );
}
